import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Search, RefreshCcw } from "lucide-react";
import { listSubmissions, updateSubmission, submissionsCsvUrl } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "contact", label: "Contact" },
  { key: "mission", label: "Join the Mission" },
  { key: "newsletter", label: "Newsletter" },
];

const STATUS_CHOICES = ["new", "in_progress", "resolved", "spam"];

export default function AdminSubmissions() {
  const { user } = useAuth();
  const canEdit = user?.role === "owner" || user?.role === "editor";
  const [params, setParams] = useSearchParams();
  const tab = params.get("t") || "contact";
  const setTab = (t) => setParams({ t });
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await listSubmissions(tab, { search: search || undefined })); }
    catch { toast.error("Unable to load submissions"); }
    finally { setLoading(false); }
  }, [tab, search]);

  useEffect(() => { load(); /* eslint-disable-line */ }, [tab]);

  const patch = async (id, patchPayload) => {
    try {
      const updated = await updateSubmission(tab, id, patchPayload);
      setItems(prev => prev.map(x => x.id === id ? updated : x));
      toast.success("Updated");
    } catch { toast.error("Update failed"); }
  };

  const download = () => { window.location.href = submissionsCsvUrl(tab); };

  return (
    <div data-testid="admin-submissions-page">
      <PageHeader eyebrow="Submissions" title="Form submissions" note="Contact enquiries, Join-the-Mission applications and newsletter subscribers. No emails are sent from here — outreach is manual." />

      <div className="mt-6 flex gap-2 border-b" style={{ borderColor: "var(--gi-border)" }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className="px-4 py-2 text-sm border-b-2"
            style={{ borderColor: tab === t.key ? "var(--gi-maroon)" : "transparent", color: tab === t.key ? "var(--gi-maroon)" : "var(--gi-muted)" }}
            data-testid={`submissions-tab-${t.key}`}
          >{t.label}</button>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px] flex gap-2">
          <input className="gi-input flex-1" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} data-testid="submissions-search-input" />
          <button onClick={load} className="btn-ghost" data-testid="submissions-search-btn"><Search size={12} /></button>
        </div>
        <button onClick={load} className="btn-ghost" title="Refresh"><RefreshCcw size={12} /></button>
        {canEdit && <button onClick={download} className="btn-outline" data-testid="submissions-export-btn"><Download size={12} /> Export CSV</button>}
      </div>

      {tab === "newsletter" ? (
        <NewsletterTable items={items} loading={loading} />
      ) : (
        <MessageTable items={items} loading={loading} tab={tab} canEdit={canEdit} onPatch={patch} />
      )}
    </div>
  );
}

const MessageTable = ({ items, loading, tab, canEdit, onPatch }) => (
  <div className="mt-6 border bg-white" style={{ borderColor: "var(--gi-border)" }}>
    <table className="w-full text-sm">
      <thead className="text-xs uppercase" style={{ background: "var(--gi-ivory-2)", color: "var(--gi-muted)" }}>
        <tr>
          <th className="text-left px-4 py-3">Created</th>
          <th className="text-left px-4 py-3">Name</th>
          <th className="text-left px-4 py-3">Email</th>
          {tab === "contact" && <th className="text-left px-4 py-3">Subject</th>}
          {tab === "mission" && <th className="text-left px-4 py-3">Path</th>}
          <th className="text-left px-4 py-3">Message</th>
          <th className="text-left px-4 py-3">Status</th>
          <th className="text-left px-4 py-3">Notes</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && !loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-sm" style={{ color: "var(--gi-muted)" }}>No submissions.</td></tr>}
        {items.map(r => (
          <tr key={r.id} className="border-t" style={{ borderColor: "var(--gi-divider)" }} data-testid={`submissions-row-${r.id}`}>
            <td className="px-4 py-3 text-xs" style={{ color: "var(--gi-muted)" }}>{new Date(r.created_at).toLocaleString()}</td>
            <td className="px-4 py-3">{r.name}</td>
            <td className="px-4 py-3"><a href={`mailto:${r.email}`} className="underline text-xs">{r.email}</a></td>
            {tab === "contact" && <td className="px-4 py-3 text-xs">{r.subject || "—"}</td>}
            {tab === "mission" && <td className="px-4 py-3 text-xs">{r.path || "—"}</td>}
            <td className="px-4 py-3 text-xs max-w-[380px] truncate" title={r.message}>{r.message}</td>
            <td className="px-4 py-3">
              <select className="gi-input" style={{ width: 130 }} value={r.status || "new"} disabled={!canEdit} onChange={e => onPatch(r.id, { status: e.target.value })} data-testid={`submissions-status-${r.id}`}>
                {STATUS_CHOICES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="px-4 py-3">
              <input className="gi-input" placeholder="Notes…" defaultValue={r.notes || ""} disabled={!canEdit} onBlur={e => e.target.value !== (r.notes || "") && onPatch(r.id, { notes: e.target.value })} data-testid={`submissions-notes-${r.id}`} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const NewsletterTable = ({ items, loading }) => (
  <div className="mt-6 border bg-white" style={{ borderColor: "var(--gi-border)" }}>
    <table className="w-full text-sm">
      <thead className="text-xs uppercase" style={{ background: "var(--gi-ivory-2)", color: "var(--gi-muted)" }}>
        <tr>
          <th className="text-left px-4 py-3">Created</th>
          <th className="text-left px-4 py-3">Name</th>
          <th className="text-left px-4 py-3">Email</th>
          <th className="text-left px-4 py-3">Country</th>
          <th className="text-left px-4 py-3">Language</th>
          <th className="text-left px-4 py-3">Interests</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && !loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm" style={{ color: "var(--gi-muted)" }}>No subscribers.</td></tr>}
        {items.map(r => (
          <tr key={r.id} className="border-t" style={{ borderColor: "var(--gi-divider)" }}>
            <td className="px-4 py-3 text-xs" style={{ color: "var(--gi-muted)" }}>{new Date(r.created_at).toLocaleString()}</td>
            <td className="px-4 py-3">{r.name}</td>
            <td className="px-4 py-3 text-xs">{r.email}</td>
            <td className="px-4 py-3">{r.country || "—"}</td>
            <td className="px-4 py-3">{r.language || "—"}</td>
            <td className="px-4 py-3 text-xs">{(r.interests || []).join(", ")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
